import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DELAY_PER_LETTER = 110;
const START_DELAY = 300;

const Portfolio: FC = () => {
  const { t } = useTranslation();
  const introText = "Saleem Enterprises"; // keep this in English as requested
  const TOTAL = introText.length;
  const navigate = useNavigate();
  const [revealedCount, setRevealedCount] = useState(0);
  const [slideUp, setSlideUp] = useState(false);
  const [showMain, setShowMain] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden"; // lock scroll

    return () => {
      document.body.style.overflow = "auto"; // cleanup
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    const timers: ReturnType<typeof setTimeout>[] = [];

    introText.split('').forEach((_, i) => {
      const t = setTimeout(() => {
        setRevealedCount(i + 1);

        if (i === TOTAL - 1) {
          const lt = setTimeout(() => {
            setSlideUp(true);

            setTimeout(() => {
              setShowMain(true);
              document.body.style.overflow = "auto"; // 🔥 unlock here
            }, 200);

          }, 600);

          timers.push(lt);
        }
      }, DELAY_PER_LETTER * i + START_DELAY);

      timers.push(t);
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  const linePct = (revealedCount / TOTAL) * 100;

  return (
    <div className="w-full h-screen relative overflow-hidden">

      {/* ── INTRO SCREEN ── */}
      <div
        className="absolute inset-0 z-20 flex items-center justify-center"
        style={{
          backgroundColor: '#002f37',
          transform: slideUp ? 'translateY(-100%)' : 'translateY(0)',
          opacity: slideUp ? 0 : 1,
          transition: 'transform 0.9s cubic-bezier(0.76,0,0.24,1), opacity 0.9s ease',
          pointerEvents: slideUp ? 'none' : 'auto',
        }}
      >
        <div style={{ textAlign: 'center', width: '90%', maxWidth: '900px' }}>
          <h1
            style={{
              fontSize: 'clamp(1.2rem, 5vw, 4rem)',
              fontWeight: 600,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              margin: 0,
              display: 'flex',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {introText.split('').map((char, i) => (
              <span
                key={i}
                style={{
                  color: i < revealedCount ? '#ffffff' : '#002f37',
                  transition: 'color 0.25s ease',
                  display: 'inline-block',
                  whiteSpace: 'pre',
                }}
              >
                {char}
              </span>
            ))}
          </h1>

          <div style={{ margin: '0.6rem auto 0', height: '2px', width: '100%', background: '#002f37', overflow: 'hidden' }}>
            <div style={{ height: '2px', background: 'white', width: `${linePct}%`, transition: 'width 0.15s linear' }} />
          </div>
        </div>
      </div>

      {/* ── MAIN SCREEN ── */}
      <div
        className="absolute inset-0 z-10"
        style={{
          transform: showMain ? 'translateY(0)' : 'translateY(30px)',
          opacity: showMain ? 1 : 0,
          transition: 'transform 0.9s cubic-bezier(0.76,0,0.24,1) 0.1s, opacity 0.9s ease 0.1s',
        }}
      >
        <div className="w-full h-full relative overflow-hidden">
          {/* Background Image */}
          {/* Background Image */}
          <img
            src="/greenhouseproject.jpeg"
            alt="Portfolio"
            className="w-full h-full object-cover"
          />

          {/* Big Background Text */}
          {/* Big Background Text */}
          <div className="absolute top-72 sm:top-40 left-0 right-0 flex justify-center pointer-events-none z-10 px-4">
            <h1 className="
    text-[2.3rem]
    sm:text-[5rem]
    md:text-[7rem]
    lg:text-[8.6rem]
    font-bold
    tracking-widest
    uppercase
    select-none
    text-white/50
    whitespace-nowrap
    leading-none
  ">
              {t('portfolio_background_heading')}
            </h1>
          </div>

          {/* Liquid Glass Card */}
          <div
            className="absolute bottom-10 left-6 sm:bottom-14 sm:left-10 md:bottom-16 md:left-17
                       w-[78vw] sm:w-72 md:w-80 lg:w-96
                       rounded-2xl p-4 sm:p-5 overflow-hidden"
            style={{
              background: `linear-gradient(135deg,
                rgba(255,255,255,0.18) 0%,
                rgba(255,255,255,0.06) 40%,
                rgba(180,255,200,0.08) 70%,
                rgba(255,255,255,0.04) 100%)`,
              backdropFilter: 'blur(5px) saturate(120%)',
              WebkitBackdropFilter: 'blur(5px) saturate(120%)',
              border: '1px solid rgba(255,255,255,0.22)',
              // boxShadow: `inset 0 1px 0 rgba(255,255,255,0.30),
              //   inset 0 -1px 0 rgba(0,0,0,0.10),
              //   0 8px 32px rgba(0,0,0,0.35),
              //   0 2px 8px rgba(0,0,0,0.20)`,
            }}
          >
            {/* Shine streak */}
            <div className="absolute top-0 left-[10%] w-[60%] h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)' }} />

            {/* Gloss layer */}
            <div className="absolute top-0 left-0 right-0 rounded-t-2xl pointer-events-none"
              style={{ height: '45%', background: 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, transparent 100%)' }} />

            {/* <h2 className="text-2xl sm:text-[1.7rem] font-semibold leading-snug mb-2 tracking-wide"
              style={{ color: 'rgba(255,255,255,0.95)' }}>
              Glass GreenHouses
            </h2> */}

            <p className="text-[0.78rem] sm:text-[0.82rem] leading-relaxed mb-4" style={{ color: 'white' }}>
              {t('portfolio_hero_desc')}
            </p>

            <div className="h-px mb-3" style={{ background: 'linear-gradient(90deg, white, transparent)' }} />

            <button
              onClick={() => navigate('/green-house')}
              className="text-[0.65rem] tracking-[0.18em] uppercase rounded-lg px-4 py-1.5 transition-all duration-200 cursor-pointer"
              style={{ color: 'white', background: 'rgba(255,255,255,0.02)', border: '1px solid white' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
            >
              {t('portfolio_hero_button')}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Portfolio;